import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getLoginUser, getUserCustomSetting, patchUserCustomSetting, UserCustomSetting } from "../api/user";

import styled from "styled-components";
import { Layout, Modal, Button, Input, Switch, Tag, Space, Tooltip, Popconfirm, message, Result, Form, notification, Dropdown, Select } from "antd";
import UploadProgressModal from "../component/upload/UploadProgressModal";
import { isUploadCancelled, useTusCancel } from "../hooks/useTusCancel";
import type { MenuProps } from "antd";

import { ViewerContext } from "./Viewer";
import { useRecoilState } from "recoil";

import { currentSlideAtom, initialSlide, currentPostAtom } from "../atom";
import { getSlideData, downloadSlide, deleteSlide, putSlideOrderByList, Slide } from "../api/slideAPI";
import {
  MediaGroup,
  createMediaGroup,
  getMediaGroups,
  moveMediaToGroup,
  setMediaGroupItems,
} from "../api/mediaGroupAPI";
import {
  GroupFilter,
  UNGROUPED_FILTER,
  buildMembership,
  countByGroup,
  groupIdOf,
  matchesGroupFilter,
} from "../component/post/mediaGroup";
import { MediaGroupAssignModal } from "../component/post";
import { deletePost, postPost, getPostData, Post, viewPost } from "../api/postAPI";

import { DeleteOutlined, LoadingOutlined, LeftOutlined, RightOutlined, EditOutlined, CheckOutlined, CloseOutlined, PlusOutlined, UnorderedListOutlined, LinkOutlined, SwapOutlined, MinusCircleOutlined } from "@ant-design/icons";
import SlideEditModal from "../component/post/modal/SlideEditModal";
import { checkUser, checkUserName } from "../api/authAPI";
import FileSaver from "file-saver";
import axios from "axios";
import { AnnotationArea, CommentArea, InformationArea, ConsultationViewer } from "../component/consultation";

import FileUploadInput from "../component/upload/FileUploadInput";
import { useSlideQC } from "../hooks/useSlideQC";
import { SlideQCModal } from "../component/upload/SlideQCModal";
import { ACCESS_TOKEN } from "../lib/cookie";
import { createSlideUpload } from "../lib/tusUpload";
import { createTempLinkForAdmin } from "../api/templinkAPI";
import { hasTempViewerSession } from "../api/api";

const HEAVY_PROCESS_EXTENSIONS = ['zip', 'mrxs', 'ndpi'];
const NEW_GROUP_VALUE = "__new__";

interface ProcessStatus {
  total: number;
  remaining: number;
}

interface ConsultationV2Props {
  isSharedView?: boolean;
}

const ConsultationV2 = ({ isSharedView = false }: ConsultationV2Props) => {
  const [currentPost, setCurrentPost] = useRecoilState<any>(currentPostAtom);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [userData, setUserData] = useState<any>({ name: "", role: "" });
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentSlide, setCurrentSlide] = useRecoilState(currentSlideAtom);
  const [annotationList, setAnnotationList] = useState<any[]>([]);
  const [commentList, setCommentList] = useState<any[]>([]);
  const [showDownloadBtn, setShowDownloadBtn] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleInput, setEditTitleInput] = useState("");
  const [loading, setLoading] = useState(false);
  const { boardId } = useParams();

  const [form] = Form.useForm();
  const [showSlideUploadModal, setShowSlideUploadModal] = useState<boolean>(false);
  const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
  const [currentProgress, setCurrentProgress] = useState<number>(0);
  const [totalProgress, setTotalProgress] = useState<number>(0);
  const [uploadingFileName, setUploadingFileName] = useState<string>("");
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [processingStatus, setProcessingStatus] = useState<Record<string, ProcessStatus>>({});
  const { showQCModal, qcItems, qcDone, qcHasIssue, runQC, closeQCModal } = useSlideQC();
  const tusCancel = useTusCancel();

  /** 전송하지 못한 파일 수만큼 "처리 중" 카운트를 되돌린다 (안 하면 게시물이 계속 처리 중으로 잠김) */
  const releaseProcessingStatus = (postId: number, unsentCount: number) => {
    if (unsentCount <= 0) return;
    setProcessingStatus(prev => {
      const current = prev[postId];
      if (!current) return prev;
      return {
        ...prev,
        [postId]: {
          total: Math.max(0, current.total - unsentCount),
          remaining: Math.max(0, current.remaining - unsentCount),
        },
      };
    });
  };
  const [useGroupId, setUseGroupId] = useState<boolean>(false);
  // 업로드 후 배정할 그룹. number = 기존 그룹, NEW_GROUP_VALUE = 새로 만들기
  const [groupTarget, setGroupTarget] = useState<number | string | null>(null);
  const [newGroupName, setNewGroupName] = useState<string>("");
  const [activeGroupFilter, setActiveGroupFilter] = useState<GroupFilter>(null);
  const [isTempViewer] = useState<boolean>(hasTempViewerSession());
  const [demoUrl, setDemoUrl] = useState<string>("");
  const [showDemoModal, setShowDemoModal] = useState<boolean>(false);
  const [demoPassword, setDemoPassword] = useState<string>("");
  const [demoExpiredAt, setDemoExpiredAt] = useState<string>("");
  const [isDemoGenerating, setIsDemoGenerating] = useState<boolean>(false);

  const [slideListEditModalOpen, setSlideListEditModalOpen] = useState(false);
  const [orderSlideList, setOrderSlideList] = useState<Slide[]>([]);
  const [orderChangeReqData, setOrderChangeReqData] = useState<any[]>();

  // 미디어 그룹(/api/media-groups) — 자문 화면은 슬라이드만 다룬다
  const [mediaGroups, setMediaGroups] = useState<MediaGroup[]>([]);
  const [groupChangeTargetId, setGroupChangeTargetId] = useState<number | null>(null);

  const [userCustomSet, setUserCustomSet] = useState<UserCustomSetting>({
    useScaleBar: true,
    useMagnificationBar: true,
    useCaptureButton: true,
    useViewerRotationSlider: true,
    useGammaSlider: true,
    useViewerThumbnail: true,
    useAnnotation: true,
    useICCButton: true,
    userGammaValue: 1,
    useInvertedZoom: false,
  });

  const location = useLocation();
  const navigate = useNavigate();

  const fetchPost = async () => {
    if (!boardId) return;

    try {
      setLoading(true);
      const postData = await getPostData(Number(boardId));
      viewPost(Number(boardId));

      if (!postData || Object.keys(postData).length === 0) {
        setCurrentPost(null);
      } else {
        setCurrentPost(postData);
        setMediaGroups(await getMediaGroups(Number(boardId), "SLIDE"));
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        setIsUnauthorized(true);
      } else {
        console.error("게시글을 불러오는데 실패했습니다.", error);
        message.error("게시글을 불러오는데 실패했습니다.");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * 슬라이드를 그룹으로 이동/해제. newGroupName 이 오면 그 이름으로 새 그룹을 만들면서 배정한다.
   * (items 교체 API 는 다른 그룹 소속 미디어를 받지 않으므로 moveMediaToGroup 이 원 그룹에서 먼저 뺀다)
   */
  const handleGroupChange = async (
    mediaIds: number[],
    targetGroupId: number | null,
    newGroupName?: string
  ) => {
    if (!boardId || mediaIds.length === 0) return;
    const allSlides: any[] = currentPost?.slides ?? [];
    const membership = buildMembership(allSlides, "SLIDE");

    if (newGroupName) {
      await moveMediaToGroup(mediaIds, null, membership);
      await createMediaGroup({
        postId: Number(boardId),
        mediaType: "SLIDE",
        name: newGroupName,
        mediaIds,
      });
    } else {
      await moveMediaToGroup(mediaIds, targetGroupId, membership);
    }
    message.success(
      targetGroupId == null && !newGroupName ? "그룹이 해제되었습니다." : "그룹이 변경되었습니다."
    );

    await fetchPost();

    const updatedPost = await getPostData(Number(boardId));
    const newSlides: any[] = updatedPost?.slides ?? [];
    if (newSlides.length === 0) return;

    // 그룹 지정 시 현재 필터 안에서 포커스 슬라이드를 자동으로 옮긴다.
    // 필터에 남은 슬라이드가 없으면 ALL 로 전환.
    const isCurrentSlideChanged = mediaIds.includes(currentSlide?.slideId);
    if (isCurrentSlideChanged && activeGroupFilter != null) {
      const remainingInFilter = newSlides.filter((s) => matchesGroupFilter(s, activeGroupFilter));
      if (remainingInFilter.length === 0) {
        setActiveGroupFilter(null);
        changeSlide(0);
      } else {
        const originalIndex = newSlides.findIndex(
          (s) => s.slideId === remainingInFilter[0].slideId
        );
        if (originalIndex !== -1) changeSlide(originalIndex);
      }
    }
  };

  const reloadAnnoList = async () => {
    if (currentSlide) {
      const data = await getSlideData(currentSlide.slideId);
      setCurrentSlide(data);
      setAnnotationList(data.annotations);
    }
  };

  const reloadComments = async () => {
    const data = await getSlideData(currentSlide.slideId);
    setCommentList(data.slideComments);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    const fileData = await downloadSlide(currentSlide.slideId);
    if(fileData === false) {
      setIsDownloading(false);
      window.alert("다운로드 권한이 없습니다.");
      return;
    }
    FileSaver.saveAs(fileData, currentPost.slides[0].filename);
    setIsDownloading(false);
  };

  const handleDeletePost = async () => {
    try {
      await deletePost(currentPost.id);
      message.success("게시글이 삭제되었습니다.");

      const hasPrevious = location.key !== "default";
      if (hasPrevious) {
        navigate(-1);
      } else {
        navigate("/");
      }
    } catch (error) {
      message.error("삭제 실패");
    }
  };

  const handleGenerateDemoUrl = async () => {
    if (!currentPost?.id) return;

    setIsDemoGenerating(true);
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 0, 0);

    const expiredAtIso = demoExpiredAt ? `${demoExpiredAt}:00` : endOfToday.toISOString();
    const uuid = await createTempLinkForAdmin({
      postId: currentPost.id,
      password: demoPassword,
      expiredAt: expiredAtIso,
    });

    const base = process.env.REACT_APP_SERVICE_URL || window.location.origin;
    const query = new URLSearchParams(location.search);
    const linkParams = new URLSearchParams();
    linkParams.set("target", "consultation");
    linkParams.set("board", "Consultation");
    linkParams.set("title", currentPost.title ?? "");
    linkParams.set("category", query.get("category") ?? String(currentPost.categoryId ?? ""));
    linkParams.set("categoryName", query.get("categoryName") ?? "");

    setDemoUrl(uuid ? `${base}/temp-link/${uuid}?${linkParams.toString()}` : "");
    setIsDemoGenerating(false);
    setShowDemoModal(false);
  };

  const handleCopyDemoUrl = async () => {
    if (!demoUrl) return;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(demoUrl);
        message.success("공유 링크를 복사했습니다.");
        return;
      } catch (error) {
        // fallback below
      }
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = demoUrl;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      message.success("공유 링크를 복사했습니다.");
    } catch (error) {
      message.error("링크 복사에 실패했습니다.");
    }
  };

  const startEditTitle = () => {
    setEditTitleInput(currentPost.title);
    setIsEditingTitle(true);
  };

  const cancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditTitleInput("");
  };

  const saveTitle = async () => {
    const nextTitle = editTitleInput.trim();

    if (!nextTitle) {
      message.warning("제목을 입력해주세요.");
      return;
    }

    try {
      await postPost(currentPost.id, nextTitle, currentPost.content);

      const query = new URLSearchParams(location.search);
      query.set("title", nextTitle);

      setCurrentPost((prev: any) => ({ ...prev, title: nextTitle }));
      setIsEditingTitle(false);
      navigate(
        {
          pathname: location.pathname,
          search: `?${query.toString()}`,
        },
        { replace: true }
      );
      message.success("제목이 수정되었습니다.");
    } catch (error) {
      console.error(error);
      message.error("제목 수정에 실패했습니다.");
    }
  };

  const changeSlide = async (newIndex: number) => {
    if (!currentPost || !currentPost.slides || currentPost.slides.length === 0) return;

    const slide = currentPost.slides[newIndex];

    try {
      setLoading(true);
      await axios.post(`${process.env.REACT_APP_DEEPZOOM_URL}/api/hottile?path=${currentPost.id}/${slide.filename}`);
    } catch (error) {
      console.error("Hot tile precaching failed", error);
    }

    setCurrentIndex(newIndex);
    const data = await getSlideData(slide.slideId);
    setCurrentSlide(data);
    setCommentList(data.slideComments);
    setAnnotationList(data.annotations);
    setLoading(false);
  };

  const filteredSlideIndices = useMemo(() => {
    if (!currentPost?.slides) return [];
    return currentPost.slides
      .map((s: any, i: number) => ({ s, i }))
      .filter(({ s }: any) => matchesGroupFilter(s, activeGroupFilter))
      .map(({ i }: any) => i);
  }, [currentPost?.slides, activeGroupFilter]);

  // 그룹별 개수는 목록 API 가 주지 않으므로 슬라이드 목록에서 센다
  const groupCounts = useMemo(() => countByGroup(currentPost?.slides ?? []), [currentPost?.slides]);
  const hasGroups = mediaGroups.length > 0;

  const handlePrevSlide = () => {
    if (!filteredSlideIndices.length) return;
    const pos = filteredSlideIndices.indexOf(currentIndex);
    const prevIndex = pos <= 0
      ? filteredSlideIndices[filteredSlideIndices.length - 1]
      : filteredSlideIndices[pos - 1];
    changeSlide(prevIndex);
  };

  const handleNextSlide = () => {
    if (!filteredSlideIndices.length) return;
    const pos = filteredSlideIndices.indexOf(currentIndex);
    const nextIndex = pos >= filteredSlideIndices.length - 1
      ? filteredSlideIndices[0]
      : filteredSlideIndices[pos + 1];
    changeSlide(nextIndex);
  };

  const handleDeleteSlide = (slideId: number, index: number) => {
    Modal.confirm({
      title: "슬라이드 삭제",
      content: "정말로 이 슬라이드를 삭제하시겠습니까?",
      okText: "삭제",
      okType: "danger",
      cancelText: "취소",
      onOk: async () => {
        try {
          setLoading(true);
          await deleteSlide(slideId);
          message.success("슬라이드가 삭제되었습니다.");
          if (currentIndex === index) {
            setCurrentIndex(0);
          } else if (currentIndex > index) {
            setCurrentIndex(currentIndex - 1);
          }
          await fetchPost();
        } catch (error) {
          console.error("Slide deletion error:", error);
          message.error("슬라이드 삭제에 실패했습니다.");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const openSlideEditModal = () => {
    setOrderSlideList(currentPost.slides);
    setSlideListEditModalOpen(true);
  };

  const closeSlideEditModal = () => setSlideListEditModalOpen(false);

  const handleSlideOrderDragEnd = (result: any) => {
    if (!result.destination) return;
    const { source, destination } = result;

    const reorderedList = [...orderSlideList];
    const [removed] = reorderedList.splice(source.index, 1);
    reorderedList.splice(destination.index, 0, removed);
    setOrderSlideList(reorderedList);

    setOrderChangeReqData(
      reorderedList.map((item, i) => ({ listOrder: i + 1, slideId: item.slideId }))
    );
  };

  const handleSlideOrderOk = async () => {
    try {
      if (orderChangeReqData && orderChangeReqData.length > 0) {
        await putSlideOrderByList(orderChangeReqData);
      }
      await fetchPost();
      setCurrentIndex(0);
      closeSlideEditModal();
    } catch (err) {
      console.error(err);
      message.error("슬라이드 순서 변경에 실패했습니다.");
    }
  };

  const handleSlideDeleteInModal = async (slideId: number) => {
    setOrderSlideList((prev) => prev.filter((item) => item.slideId !== slideId));
    await deleteSlide(slideId);
  };

  const uploadFile = async (
    postId: number,
    file: any,
    fileIndex: number,
    totalFileCount: number,
    tracker: { successCount: number; notified: boolean },
    postTitle: string,
    hasHeavyFile: boolean,
  ) => {
    setUploadingFileName(file.name);
    setCurrentFileIndex(fileIndex + 1);

    return new Promise<void>((resolve, reject) => {
      let isResolved = false; 

      const upload = createSlideUpload(file, {
        endpoint: process.env.REACT_APP_BACKEND_URL + "/api/slide/tus",
        headers: { Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN)}` },
        chunkSize: 1024 * 1024 * 5,
        // 이어올리기를 쓰지 않으므로 이어올리기 정보도 저장하지 않는다
        // (남기면 AddSlide 에서 같은 파일을 올릴 때 이 업로드의 게시물로 이어올려질 수 있다)
        storeFingerprintForResuming: false,
        metadata: {
          postId: String(postId),
          filename: file.name || "",
          slidename: "",
          caption: "",
          gender: "",
          age: "",
          site: "",
          zindanName: "",
        },
        onError: reject,
        onProgress: function (bytesUploaded, bytesTotal) {
          const currentFilePercentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
          setCurrentProgress(Number(currentFilePercentage));

          const completedFiles = fileIndex;
          const overallPercentage = ((completedFiles + (bytesUploaded / bytesTotal)) / totalFileCount * 100).toFixed(2);
          setTotalProgress(Number(overallPercentage));

          if (bytesUploaded === bytesTotal) {
            if (!isResolved) {
              isResolved = true;
              tusCancel.markSent(upload);
              setTimeout(() => {
                resolve();
              }, 500);
            }
          }
        },
        onSuccess: async () => {
          tracker.successCount++;

          setProcessingStatus(prev => {
            const currentStatus = prev[postId] || { total: 0, remaining: 0 };
            const newRemaining = currentStatus.remaining > 0 ? currentStatus.remaining - 1 : 0;

            return {
              ...prev,
              [postId]: { ...currentStatus, remaining: newRemaining }
            };
          });

          if (tracker.successCount === totalFileCount && !tracker.notified) {
             tracker.notified = true; 

             await fetchPost(); 

             if (hasHeavyFile) {
               notification.success({
                 key: `upload-complete-${postId}`,
                 message: '처리 완료',
                 description: `'${postTitle}'의 모든 파일 처리가 완료되어 조회가 가능합니다.`,
                 duration: 5,
               });
             }
          }
        }
      }, { isCancelled: tusCancel.isCancelled });
      tusCancel.track(upload, reject);
      upload.start();
    });
  };

  /**
   * 업로드가 끝난 뒤 새로 생긴 슬라이드를 그룹에 배정한다.
   * 새 슬라이드는 항상 그룹없음 상태이므로 원 그룹에서 빼는 과정은 필요 없다.
   */
  const assignUploadedToGroup = async (uploadPostId: number, prevSlideIds: Set<number>) => {
    if (!useGroupId || groupTarget == null) return;
    if (groupTarget === NEW_GROUP_VALUE && !newGroupName.trim()) return;

    try {
      const post = await getPostData(uploadPostId);
      const allSlides: any[] = post?.slides ?? [];
      const newIds = allSlides
        .map((s) => s.slideId)
        .filter((sid: number) => sid != null && !prevSlideIds.has(sid));
      if (newIds.length === 0) return;

      if (groupTarget === NEW_GROUP_VALUE) {
        await createMediaGroup({
          postId: uploadPostId,
          mediaType: "SLIDE",
          name: newGroupName.trim(),
          mediaIds: newIds,
        });
      } else {
        // items 는 교체 방식이라 기존 멤버를 함께 보내야 한다
        const gid = Number(groupTarget);
        const existing = allSlides.filter((s) => groupIdOf(s) === gid).map((s) => s.slideId);
        await setMediaGroupItems(gid, Array.from(new Set([...existing, ...newIds])));
      }
    } catch (e: any) {
      console.error("uploaded slide group assign error:", e);
      message.error(e?.message || "업로드는 완료됐지만 그룹 배정에 실패했습니다.");
    }
  };

  const handleFileUpload = async (values: any) => {
    // 중지·오류 처리에서 쓰는 업로드 진행 정보
    let uploadPostId: number | null = null;
    let uploadTotalCount = 0;
    let sentCount = 0;
    try {
      if (values.file && values.file.fileList.length > 0) {
        const fileList = values.file.fileList;
        const postId = currentPost.id;
        const postTitle = currentPost.title;
        const prevSlideIds = new Set<number>((currentPost.slides || []).map((s: any) => s.slideId as number));

        const hasHeavyFile = fileList.some((item: any) => {
            const ext = item.name.split('.').pop()?.toLowerCase();
            return ext && HEAVY_PROCESS_EXTENSIONS.includes(ext);
        });

        setProcessingStatus(prev => ({
          ...prev,
          [postId]: { total: fileList.length, remaining: fileList.length }
        }));

        setTotalFiles(fileList.length);
        setShowProgressModal(true);
        setCurrentProgress(0);
        setTotalProgress(0);
        setCurrentFileIndex(0);
        tusCancel.begin();
        uploadPostId = postId;
        uploadTotalCount = fileList.length;

        const tracker = { successCount: 0, notified: false };

        for (let i = 0; i < fileList.length; i++) {
          const file = fileList[i];
          tusCancel.throwIfCancelled();
          await uploadFile(postId, file.originFileObj, i, fileList.length, tracker, postTitle, hasHeavyFile);
          sentCount++;
        }

        // 업로드된 슬라이드를 선택한 그룹에 배정 (TUS 메타데이터 groupId 대체)
        await assignUploadedToGroup(postId, prevSlideIds);

        setShowProgressModal(false);
        setShowSlideUploadModal(false);
        form.resetFields();
        setUseGroupId(false);
        setGroupTarget(null);
        setNewGroupName("");
        runQC(postId, prevSlideIds, fileList.map((item: any) => item.originFileObj?.name || item.name));

        if (hasHeavyFile) {
            notification.info({
                message: '대용량 파일 전송 완료',
                description: '파일 전송은 완료되었으나, 서버 처리에 시간이 소요될 수 있습니다.',
                duration: 5,
                placement: 'bottomRight',
            });
        } else {
            message.success("파일 추가가 완료되었습니다.");
        }
      } else {
          message.warning("업로드할 파일을 선택해주세요.");
      }
    } catch (error) {
      setShowProgressModal(false);
      setShowSlideUploadModal(false);
      if (uploadPostId != null) releaseProcessingStatus(uploadPostId, uploadTotalCount - sentCount);

      if (isUploadCancelled(error)) {
        // 중지: 이미 전송된 파일만 등록된다. 그룹 배정·QC 등 후속 작업은 하지 않는다
        form.resetFields();
        setUseGroupId(false);
        setGroupTarget(null);
        setNewGroupName("");
        notification.warning({
          message: "업로드를 중지했습니다",
          description:
            sentCount > 0
              ? `전체 ${uploadTotalCount}개 중 전송이 끝난 ${sentCount}개 파일은 등록됩니다. 필요 없으면 목록에서 삭제해주세요.`
              : "등록된 파일은 없습니다.",
          duration: 8,
          placement: "bottomRight",
        });
        fetchPost();
        return;
      }
      console.error("Upload error:", error);
      message.error("업로드 중 오류가 발생했습니다.");
    }
  };

  useEffect(() => {
    if (isTempViewer) {
      setUserData({ name: "", role: "TEMP_VIEWER" });
      return;
    }

    getLoginUser().then((data) => {
      if (!data || !data.name) {
        navigate('/login', { state: { from: location } });
        return;
      }
      setUserData({ name: data.name, role: data.role });
    });

    const getUserSetting = async () => {
      const userSet = await getUserCustomSetting();
      return setUserCustomSet(userSet);
    };

    getUserSetting();

    checkUser().then((role) => {
      setShowDownloadBtn(role === "ADMIN_MAIN" || role === "ADMIN_SUB" || role === "ADVISOR");
    });
  }, [isTempViewer, location, navigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = 
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable;
      if (isInput) return; // 입력 필드에서는 방향키 기능 비활성화
      if (isEditingTitle) return;

      if (!filteredSlideIndices.length) return;

      if (e.key === "ArrowLeft") {
        handlePrevSlide();
      } else if (e.key === "ArrowRight") {
        handleNextSlide();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentIndex, filteredSlideIndices, isEditingTitle]);

  useEffect(() => {
    if (currentPost && currentPost.slides && currentPost.slides[0]) {
      const initSlide = async () => {
        try {
          setLoading(true);
          const slide = currentPost.slides[0];
          await axios.post(`${process.env.REACT_APP_DEEPZOOM_URL}/api/hottile?path=${currentPost.id}/${slide.filename}`);

          const slideId = slide.slideId;
          const data = await getSlideData(slideId);
          setCurrentSlide(data);
          setAnnotationList(data.annotations);
          setCommentList(data.slideComments);
        } catch (error) {
          console.error(error);
          if (currentPost.slides[0]) {
            const data = await getSlideData(currentPost.slides[0].slideId);
            setCurrentSlide(data);
            setAnnotationList(data.annotations);
            setCommentList(data.slideComments);
          }
        } finally {
          setLoading(false);
        }
      };

      initSlide();
    } else {
      setCurrentSlide(initialSlide);
    }
  }, [currentPost?.id, currentPost?.slides?.length, setCurrentSlide]);

  useEffect(() => {
    if (isTempViewer) return;

    const updateUserSet = async () => {
      await patchUserCustomSetting(userCustomSet);
    };

    window.addEventListener("beforeunload", updateUserSet);

    return () => {
      window.removeEventListener("beforeunload", updateUserSet);
    };
  }, [userCustomSet, isTempViewer]);

  useEffect(() => {
    if (isTempViewer) return;

    const handleOutPath = async () => {
      await patchUserCustomSetting(userCustomSet);
    };

    return () => {
      handleOutPath();
    };
  }, [location, location.pathname, userCustomSet, isTempViewer]);

  useEffect(() => {
    fetchPost();
  }, [boardId]);

  if (isUnauthorized) {
    return (
      <Layout.Content style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Result
          status="403"
          title="401 Unauthorized"
          subTitle="해당 게시글을 열람할 권한이 없습니다."
          extra={
            <Button type="primary" onClick={() => navigate("/")}>
              메인으로 돌아가기
            </Button>
          }
        />
      </Layout.Content>
    );
  }

  if (loading) {
    return (
      <Layout.Content style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <LoadingOutlined style={{ fontSize: 48, color: "#1677ff" }} spin />
      </Layout.Content>
    );
  }

  if (!currentPost) {
    return (
      <Layout.Content style={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Result status="404" title="해당 게시글을 찾을 수 없습니다." subTitle="Not found" />
      </Layout.Content>
    );
  }

  const isWriter = (userData.name === currentPost.writer) || (userData.role === "ADMIN_MAIN");

  return (
    <ViewerContext.Provider value={{ userCustomSet, setUserCustomSet }}>
      <Layout.Content style={{ minHeight: "80vh" }}>
        {!isSharedView && (
          <>
            <StHeaderContainer>
              <div className="title-area">
                {isEditingTitle ? (
                  <Input
                    value={editTitleInput}
                    onChange={(e) => setEditTitleInput(e.target.value)}
                    onPressEnter={saveTitle}
                    style={{ fontSize: '1.5rem', fontWeight: 'bold', height: 'auto' }}
                  />
                ) : (
                  <StConsultationPostTitle>{currentPost.title}</StConsultationPostTitle>
                )}
              </div>

              <div className="action-area">
                <Space>
                  {userData.role === "ADMIN_MAIN" && (
                    <Tooltip title="공유 링크 생성">
                      <Button
                        type="text"
                        shape="circle"
                        icon={<LinkOutlined />}
                        onClick={() => {
                          setDemoPassword("");
                          setDemoExpiredAt("");
                          setShowDemoModal(true);
                        }}
                      />
                    </Tooltip>
                  )}
                  {isWriter && (
                    <Space>
                      {isEditingTitle ? (
                        <>
                          <Tooltip title="저장">
                            <Button type="primary" shape="circle" icon={<CheckOutlined />} onClick={saveTitle} />
                          </Tooltip>
                          <Tooltip title="취소">
                            <Button shape="circle" icon={<CloseOutlined />} onClick={cancelEditTitle} />
                          </Tooltip>
                        </>
                      ) : (
                        <>
                        <Tooltip title="슬라이드 파일 추가">
                          <Button type="text" shape="circle" icon={<PlusOutlined />} onClick={() => setShowSlideUploadModal(true)} />
                        </Tooltip>
    
                        <Tooltip title="슬라이드 순서 변경">
                          <Button type="text" shape="circle" icon={<UnorderedListOutlined />} onClick={openSlideEditModal} />
                        </Tooltip>
    
                        <Tooltip title="제목 수정">
                          <Button type="text" shape="circle" icon={<EditOutlined />} onClick={startEditTitle} />
                        </Tooltip>
    
                        <Popconfirm
                          title="게시글 삭제"
                          description="정말로 이 게시글을 삭제하시겠습니까?"
                          onConfirm={handleDeletePost}
                          okText="삭제"
                          cancelText="취소"
                          okButtonProps={{ danger: true }}
                          placement="bottom"
                        >
                          <Tooltip title="게시글 삭제">
                            <Button type="text" danger shape="circle" icon={<DeleteOutlined />} />
                          </Tooltip>
                        </Popconfirm>
                      </>
                      )}
                    </Space>
                  )}
                </Space>
              </div>
            </StHeaderContainer>
            {demoUrl && (
              <div style={{ width: "80%", margin: "0 auto 12px", display: "flex", gap: 8 }}>
                <Input readOnly value={demoUrl} />
                <Button onClick={handleCopyDemoUrl}>Copy</Button>
              </div>
            )}
          </>
        )}
        {currentPost?.slides?.[0] &&
          <>
            <StViewerContainer>
              <StInformationBox>
                <StSidebar>
                  <StSidebarItem style={{ flex: 2 }}>
                    <InformationArea />
                  </StSidebarItem>
                  <StSidebarItem style={{ flex: 3 }}>
                    <AnnotationArea annoList={annotationList} reloadAnnoList={reloadAnnoList} isSharedView={isSharedView} userRole={userData.role} />
                  </StSidebarItem>
                  <StSidebarItem style={{ flex: 5 }}>
                    <CommentArea slideId={currentSlide?.slideId} comments={commentList} reloadComments={reloadComments} isSharedView={isSharedView} userRole={userData.role} />
                  </StSidebarItem>
                </StSidebar>
              </StInformationBox>
              <ConsultationViewer postId={currentPost.id} description={currentSlide?.description} annotationList={annotationList} setAnnotationList={setAnnotationList} loading={loading} isSharedView={isSharedView} />
            </StViewerContainer>
            <div style={{ width: "80%", margin: "0 auto", marginTop: "2rem", position: "relative" }}>
              {(() => {
                if (!hasGroups) return null;
                // 필터 전환 시 해당 그룹의 첫 슬라이드로 이동
                const applyFilter = (next: GroupFilter) => {
                  setActiveGroupFilter(next);
                  const firstIndex = currentPost.slides.findIndex((s: any) =>
                    matchesGroupFilter(s, next)
                  );
                  if (firstIndex !== -1) changeSlide(firstIndex);
                };
                const badgeStyle = (active: boolean) => ({
                  background: active ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.08)",
                  borderRadius: 8,
                  padding: "0 5px",
                  fontSize: 11,
                  marginLeft: 3,
                });
                return (
                  <div style={{ marginBottom: "12px", display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                    <span style={{ fontSize: "12px", color: "#8c8c8c", marginRight: 4 }}>그룹 필터:</span>
                    <Tag
                      color={activeGroupFilter == null ? "blue" : "default"}
                      onClick={() => applyFilter(null)}
                      style={{ cursor: "pointer" }}
                    >
                      All <span style={badgeStyle(activeGroupFilter == null)}>{groupCounts.total}</span>
                    </Tag>
                    {groupCounts.ungrouped > 0 && (
                      <Tag
                        color={activeGroupFilter === UNGROUPED_FILTER ? "blue" : "default"}
                        onClick={() =>
                          applyFilter(activeGroupFilter === UNGROUPED_FILTER ? null : UNGROUPED_FILTER)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        그룹없음 <span style={badgeStyle(activeGroupFilter === UNGROUPED_FILTER)}>{groupCounts.ungrouped}</span>
                      </Tag>
                    )}
                    {mediaGroups.map((group) => {
                      const gid = group.mediaGroupId;
                      const isActive = activeGroupFilter === gid;
                      return (
                        <Tag
                          key={gid}
                          color={isActive ? "blue" : "default"}
                          onClick={() => applyFilter(isActive ? null : gid)}
                          style={{ cursor: "pointer" }}
                        >
                          {group.name} <span style={badgeStyle(isActive)}>{groupCounts.byGroup[gid] ?? 0}</span>
                        </Tag>
                      );
                    })}
                  </div>
                );
              })()}
              <Button
                shape="circle"
                icon={<LeftOutlined />}
                onClick={handlePrevSlide}
                style={{
                  position: "absolute", left: -50, top: "50%", transform: "translateY(-50%)",
                  zIndex: 10, width: 45, height: 45, backgroundColor: "rgba(30, 46, 58, 0.85)",
                  color: "#fff", border: "1px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)", transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(30, 46, 58, 1)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.25)";
                  e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(30, 46, 58, 0.85)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
                  e.currentTarget.style.transform = "translateY(-50%) scale(1)";
                }}
              />

              <Button
                shape="circle"
                icon={<RightOutlined />}
                onClick={handleNextSlide}
                style={{
                  position: "absolute", right: -50, top: "50%", transform: "translateY(-50%)",
                  zIndex: 10, width: 45, height: 45, backgroundColor: "rgba(30, 46, 58, 0.85)",
                  color: "#fff", border: "1px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)", transition: "all 0.3s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(30, 46, 58, 1)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.25)";
                  e.currentTarget.style.transform = "translateY(-50%) scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(30, 46, 58, 0.85)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
                  e.currentTarget.style.transform = "translateY(-50%) scale(1)";
                }}
              />

              {currentPost.slides
                .map((slide: any, index: number) => ({ slide, index }))
                .filter(({ slide }: any) => matchesGroupFilter(slide, activeGroupFilter))
                .map(({ slide, index }: any) => {
                const menuItems: MenuProps['items'] = [
                  {
                    key: 'changeGroup',
                    label: '그룹 지정',
                    icon: <SwapOutlined />,
                    onClick: () => setGroupChangeTargetId(slide.slideId),
                  },
                  // 그룹에 속해 있을 때만 해제 메뉴를 띄운다
                  ...(groupIdOf(slide) != null
                    ? [{
                        key: 'clearGroup',
                        label: '그룹 해제',
                        icon: <MinusCircleOutlined />,
                        onClick: () => handleGroupChange([slide.slideId], null),
                      }]
                    : []),
                  {
                    key: 'delete',
                    label: '슬라이드 삭제',
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: () => handleDeleteSlide(slide.slideId, index),
                  }
                ];

                return (
                  <Dropdown
                    key={`${slide.pathFileName}_${index}`}
                    menu={{ items: menuItems }}
                    trigger={['contextMenu']}
                    disabled={!isWriter}
                  >
                    <div style={{
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "center",
                      margin: "5px",
                      verticalAlign: "top",
                      width: "100px"
                    }}>
                      <img
                        alt={`slide_${slide.pathFileName}`}
                        src={
                          process.env.REACT_APP_BACKEND_URL +
                          "/api/photo/" +
                          slide.thumbnail
                        }
                        onClick={() => changeSlide(index)}
                        style={{
                          cursor: "pointer",
                          width: "100px",
                          height: "100px",
                          border: index === currentIndex
                              ? "3px solid #1677ff"
                              : "1px solid #d9d9d9",
                          borderRadius: 8,
                          transition: "all 0.2s",
                          objectFit: "cover"
                        }}
                      />
                      <span 
                        style={{
                          marginTop: "6px",
                          fontSize: "12px",
                          color: "#555",
                          width: "100%",
                          textAlign: "center",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          cursor: "default"
                        }}
                        title={slide.description || ""}
                      >
                        {slide.description || ""}
                      </span>
                    </div>
                  </Dropdown>
                );
              })}
            </div>

            <div style={{ width: "80%", margin: "1rem auto", textAlign: "right" }}>
              {showDownloadBtn && <Button onClick={handleDownload}>File Download</Button>}
            </div>
            {isDownloading &&
              <Modal open={isDownloading} centered closable={false} footer={null}>
                Downloading...  <LoadingOutlined />
              </Modal>
            }
          </>}

        <Modal
          title="공유 링크 생성"
          open={showDemoModal}
          onCancel={() => setShowDemoModal(false)}
          onOk={handleGenerateDemoUrl}
          okText="생성"
          cancelText="취소"
          confirmLoading={isDemoGenerating}
          centered
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Input.Password
              value={demoPassword}
              onChange={(e) => setDemoPassword(e.target.value)}
              placeholder="비밀번호를 입력하세요. 없으면 빈 값으로 두세요."
            />
            <Input
              type="datetime-local"
              value={demoExpiredAt}
              onChange={(e) => {
                const selected = e.target.value;
                if (!selected) {
                  setDemoExpiredAt("");
                  return;
                }

                const today = new Date().toISOString().slice(0, 10);
                const selectedDate = selected.slice(0, 10);
                setDemoExpiredAt(selectedDate === today ? `${today}T23:59` : selected);
              }}
            />
          </div>
        </Modal>
        <Modal
          title="슬라이드 파일 추가"
          open={showSlideUploadModal}
          onCancel={() => {
            form.resetFields();
            setUseGroupId(false);
            setShowSlideUploadModal(false);
          }}
          centered
          footer={null}
          width="40%"
        >
          <Form form={form} onFinish={handleFileUpload} layout="vertical">
            {/* 그룹은 업로드 완료 후 /api/media-groups 로 배정한다 (TUS 메타데이터 전송 X) */}
            <Form.Item label="그룹 지정" style={{ marginBottom: useGroupId ? '8px' : '16px', marginTop: "1rem" }}>
              <Switch
                checked={useGroupId}
                onChange={(checked) => {
                  setUseGroupId(checked);
                  if (checked) {
                    setGroupTarget(mediaGroups.length > 0 ? mediaGroups[0].mediaGroupId : NEW_GROUP_VALUE);
                  } else {
                    setGroupTarget(null);
                    setNewGroupName("");
                  }
                }}
              />
            </Form.Item>
            {useGroupId && (
              <Form.Item label="그룹">
                <Select
                  value={groupTarget ?? undefined}
                  onChange={(v) => setGroupTarget(v)}
                  options={[
                    ...mediaGroups.map((g) => ({ label: g.name, value: g.mediaGroupId })),
                    { label: "＋ 새 그룹 만들기", value: NEW_GROUP_VALUE },
                  ]}
                />
                {groupTarget === NEW_GROUP_VALUE && (
                  <Input
                    style={{ marginTop: 8 }}
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="새 그룹 이름"
                    maxLength={255}
                  />
                )}
              </Form.Item>
            )}
            <Form.Item
              name="file"
              label="추가할 파일"
              style={{ marginBottom: "0" }}
            >
              <FileUploadInput
                mode="all"
                multiple={true}
                showSuccessMessage={false}
              />
            </Form.Item>
            <Form.Item style={{ marginTop: "20px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={() => {
                  form.resetFields();
                  setUseGroupId(false);
                  setShowSlideUploadModal(false);
                }} style={{ marginRight: "10px" }}>
                  취소
                </Button>
                <Button type="primary" htmlType="submit">
                  업로드
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
        <UploadProgressModal
          open={showProgressModal}
          fileName={uploadingFileName}
          fileIndex={currentFileIndex}
          totalFiles={totalFiles}
          currentPercent={currentProgress}
          totalPercent={totalProgress}
          onCancel={tusCancel.cancel}
          cancelling={tusCancel.cancelling}
        />

        <MediaGroupAssignModal
          open={groupChangeTargetId != null}
          groups={mediaGroups}
          selectedCount={1}
          itemLabel="슬라이드"
          allowUngroup={false}
          currentGroupId={groupIdOf(
            (currentPost?.slides ?? []).find((s: any) => s.slideId === groupChangeTargetId)
          )}
          onCancel={() => setGroupChangeTargetId(null)}
          onSubmit={async ({ targetGroupId, newGroupName }) => {
            if (groupChangeTargetId == null) return;
            await handleGroupChange([groupChangeTargetId], targetGroupId, newGroupName);
            setGroupChangeTargetId(null);
          }}
        />

        {/* <SlideEditModal
          open={slideListEditModalOpen}
          orderSlideList={orderSlideList}
          onOk={handleSlideOrderOk}
          onCancel={closeSlideEditModal}
          onDragEnd={handleSlideOrderDragEnd}
          onDeleteSlide={handleSlideDeleteInModal}
        /> */}
        <SlideQCModal
          open={showQCModal}
          items={qcItems}
          done={qcDone}
          hasIssue={qcHasIssue}
          onClose={closeQCModal}
        />
      </Layout.Content>
    </ViewerContext.Provider>
  );
};

const StHeaderContainer = styled.div`
  width: 80%;
  margin: 1rem auto;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .title-area {
    flex: 1;
    margin-right: 1rem;
    max-width: 110rem; // 타이틀 너비 제한

    h1 {
      margin: 0;
      white-space: normal;
      word-break: break-word;
    }
  }

  .action-area {
    flex-shrink: 0;
  }
`;

const StViewerContainer = styled.div`
  width: 80%;
  margin: 0 auto;
  position: relative;
`;

const StInformationBox = styled.div`
  position: absolute;
  width: 100%;
  height: 70vh;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  z-index: 5;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const StSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-left: auto;
  width: 350px;
  height: 87%;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 0.7rem;
`;

const StSidebarItem = styled.div`
  width: 100%;
  pointer-events: auto;
  overflow-y: auto;
`;

const StConsultationPostTitle = styled.h1`
  
`

export default ConsultationV2;
